import { Text, View } from "react-native";
import { ActionsContainer, Button, ScreenContainer, ScrollableContainer } from "../components";
import { useQuery } from "@apollo/client";
import { getEmploymentsQuery } from "./EmploymentEditScreen/types";
import { GET_EMPLOYMENTS_QUERY } from "./EmploymentEditScreen/query";
import { ActivityIndicator } from "react-native-paper";
import { RootStackScreenProps } from "./types";
import SelectableEmploymentTile from "../components/SelectableEmploymentTile";
import { useEffect, useState } from "react";
import config from "../config";
import { useIntercom } from "../hooks";
import { Client as IntercomClient } from "../hooks/useIntercom";

const SelectPrimaryEmployment = ({ navigation, route }: RootStackScreenProps<"SelectPrimaryEmployment">) => {
  const [selectedEmployment, setSelectedEmployment] = useState("");
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const employmentToRemove = route.params?.employmentToRemove;
  const { data: employmentsData, loading: loadingEmployments } = useQuery<getEmploymentsQuery>(GET_EMPLOYMENTS_QUERY);
  const intercom = useIntercom();

  const employmentsMemberIsKeeping = employmentsData?.currentPerson?.employments.filter(
    (employment) => employment.id !== employmentToRemove
  );

  const hasOnlyOneEmployment = employmentsMemberIsKeeping?.length === 1;

  useEffect(() => {
    if (hasOnlyOneEmployment) {
      setSelectedEmployment(employmentsMemberIsKeeping[0].id);
      setButtonDisabled(false);
    }
  }, [employmentsMemberIsKeeping, hasOnlyOneEmployment]);

  if (loadingEmployments) {
    return <ActivityIndicator className="flex-1 items-center justify-center bg-white" />;
  }

  const handleSwitch = (employmentId: string) => {
    setButtonDisabled(false);
    setSelectedEmployment(employmentId);
  };

  const openEmploymentAritcle = (intercom: IntercomClient) => {
    intercom.present(config.intercomContent.primarySecondaryEmploymentArticle);
  };

  return (
    <ScreenContainer navigationTitle="Remove Employment">
      <View className="flex-1">
        <ScrollableContainer>
          <View className="p-6">
            <Text className="text-regular-tight font-700 text-ink-darkest">{`${
              hasOnlyOneEmployment ? "Primary Employment" : "Select Primary Employment"
            }`}</Text>
            <Text className="text-small-normal font-400 text-ink mt-2 mb-2">
              {`The employment you are removing is set as your primary employment. ${
                hasOnlyOneEmployment
                  ? "Once removed, your secondary employment will be set as your primary employment."
                  : "Select the secondary employment that should be assigned as primary."
              }`}
            </Text>
            <Text className="text-small-normal font-400 text-ink mt-2 mb-2">
              Primary employment determines your section affiliation and other membership details.{" "}
              <Text onPress={() => openEmploymentAritcle(intercom)} className="text-link-small text-primary underline">
                Learn More
              </Text>
            </Text>
          </View>
          <View className="px-6">
            {[...(employmentsMemberIsKeeping ?? [])]
              .sort((a, b) => (a.isPrimary === b.isPrimary ? 0 : a.isPrimary ? -1 : 1))
              .map((employment) => (
                <SelectableEmploymentTile
                  key={employment.id}
                  employment={employment}
                  selectedEmployment={selectedEmployment}
                  handleSwitch={handleSwitch}
                />
              ))}
          </View>
        </ScrollableContainer>
        <View>
          <ActionsContainer>
            <Button
              disabled={buttonDisabled}
              testID="select-primary-employment-button"
              onPress={() =>
                navigation.navigate("EmploymentRemove", { id: employmentToRemove, newPrimaryEmploymentId: selectedEmployment })
              }
              mode="contained"
            >
              Continue
            </Button>
          </ActionsContainer>
        </View>
      </View>
    </ScreenContainer>
  );
};

export default SelectPrimaryEmployment;
